import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";

/* ============ Utils ============ */
const pad2 = (n) => String(n).padStart(2, "0");

const formatDateStr = (date, pattern = "dd-mm-yyyy") => {
  if (!(date instanceof Date) || isNaN(date)) return "";
  const d = pad2(date.getDate());
  const m = pad2(date.getMonth() + 1);
  const y = date.getFullYear();
  return pattern === "dd/mm/yyyy" ? `${d}/${m}/${y}` : `${d}-${m}-${y}`;
};

const parseDateStr = (str) => {
  if (!str) return null;
  const sep = str.includes("/") ? "/" : "-";
  const [dd, mm, yyyy] = str.split(sep).map((s) => parseInt(s, 10));
  if (!dd || !mm || !yyyy) return null;
  const dt = new Date(yyyy, mm - 1, dd);
  return isNaN(dt) ? null : dt;
};

const stripTime = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

const clampDate = (date, minDate, maxDate) => {
  if (!date) return date;
  if (minDate && date < stripTime(minDate)) return stripTime(minDate);
  if (maxDate && date > stripTime(maxDate)) return stripTime(maxDate);
  return date;
};

const decadeYears = (year) => {
  const start = Math.floor(year / 10) * 10;
  return Array.from({ length: 12 }, (_, i) => start + i);
};

const buildMonthGrid = (year, month, weekStartsOn = 0) => {
  const first = new Date(year, month, 1);
  const firstDay = (first.getDay() - weekStartsOn + 7) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const arr = [];
  for (let i = 0; i < firstDay; i++) arr.push(null);
  for (let d = 1; d <= daysInMonth; d++) arr.push(new Date(year, month, d));
  return arr;
};

const defaultDaysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const defaultMonths = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const usePortalRoot = () => {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  return mounted ? document.body : null;
};

const calcPanelPos = (anchorEl, panelW, panelH, gap = 8) => {
  if (!anchorEl) return null;
  const rect = anchorEl.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  const spaceBelow = vh - rect.bottom;
  const spaceAbove = rect.top;

  const placeAbove = spaceBelow < panelH + gap && spaceAbove > spaceBelow;

  const top = placeAbove
    ? Math.max(gap, rect.top - panelH - gap)
    : Math.min(vh - gap - panelH, rect.bottom + gap);

  const left = Math.min(Math.max(gap, rect.left), vw - gap - panelW);

  return { top, left, placeAbove };
};

/* ============ Component ============ */
const DatePicker = ({
  placeholder = "Select date",
  width = "100%",
  height = "52px",

  initValue = "",
  isCalendarOpen,
  onToggle,

  value,
  onChange,
  isOpen,
  onOpenChange,

  format = "dd-mm-yyyy",
  weekStartsOn = 0,
  daysOfWeek = defaultDaysOfWeek,
  months = defaultMonths,
  todayLabel = "Hôm nay",
  clearLabel = "Xóa",

  minDate,
  maxDate,
  isDateDisabled,
}) => {
  const portalRoot = usePortalRoot();

  const isControlled = value != null;
  const externalValue = isControlled ? value : initValue;

  const [selectedStr, setSelectedStr] = useState(externalValue || "");
  const selectedDate = useMemo(() => parseDateStr(selectedStr), [selectedStr]);

  const controlledOpen = isOpen ?? isCalendarOpen;
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen != null ? controlledOpen : internalOpen;

  const [viewMode, setViewMode] = useState("days");
  const today = useMemo(() => stripTime(new Date()), []);
  const [currYear, setCurrYear] = useState(today.getFullYear());
  const [currMonth, setCurrMonth] = useState(today.getMonth());

  const [focusDate, setFocusDate] = useState(null);

  const containerRef = useRef(null);
  const controlRef = useRef(null);
  const panelRef = useRef(null);

  // panel layout constants
  const PANEL_H = 360; // đủ cho header + grid + actions
  const PANEL_W_FALLBACK = 320;

  const [panelPos, setPanelPos] = useState({ top: 0, left: 0, width: 0 });

  useEffect(() => {
    if (externalValue !== selectedStr) {
      setSelectedStr(externalValue || "");
      const parsed = parseDateStr(externalValue || "");
      if (parsed) {
        setCurrYear(parsed.getFullYear());
        setCurrMonth(parsed.getMonth());
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalValue]);

  const setOpen = useCallback(
    (next) => {
      if (onOpenChange) onOpenChange(next);
      else if (onToggle) onToggle();
      else setInternalOpen(next);
    },
    [onOpenChange, onToggle],
  );

  const openCalendar = useCallback(() => setOpen(true), [setOpen]);
  const closeCalendar = useCallback(() => setOpen(false), [setOpen]);
  const toggleCalendar = useCallback(() => setOpen(!open), [setOpen, open]);

  // Recalc panel pos when open (portal + fixed)
  const recalc = useCallback(() => {
    const anchor = controlRef.current;
    if (!anchor) return;

    const rect = anchor.getBoundingClientRect();
    const w = rect.width || PANEL_W_FALLBACK;
    const p = calcPanelPos(anchor, w, PANEL_H, 8);
    if (!p) return;

    setPanelPos({
      top: p.top,
      left: p.left,
      width: w,
      placeAbove: p.placeAbove,
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    recalc();
  }, [open, recalc, currYear, currMonth, viewMode]);

  // Outside click (consider portal)
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      const inControl = containerRef.current?.contains(e.target);
      const inPanel = panelRef.current?.contains(e.target);
      if (!inControl && !inPanel) closeCalendar();
    };
    const onWin = () => recalc();

    document.addEventListener("mousedown", onDocClick);
    window.addEventListener("resize", onWin);
    window.addEventListener("scroll", onWin, true);

    return () => {
      document.removeEventListener("mousedown", onDocClick);
      window.removeEventListener("resize", onWin);
      window.removeEventListener("scroll", onWin, true);
    };
  }, [open, closeCalendar, recalc]);

  const daysGrid = useMemo(
    () => buildMonthGrid(currYear, currMonth, weekStartsOn),
    [currYear, currMonth, weekStartsOn],
  );

  const isInRange = (date) => {
    if (!date) return false;
    const d = stripTime(date);
    if (minDate && d < stripTime(minDate)) return false;
    if (maxDate && d > stripTime(maxDate)) return false;
    if (typeof isDateDisabled === "function" && isDateDisabled(d)) return false;
    return true;
  };

  const changeMonth = (delta) => {
    let m = currMonth + delta;
    let y = currYear;
    if (m < 0) {
      m = 11;
      y -= 1;
    } else if (m > 11) {
      m = 0;
      y += 1;
    }
    setCurrMonth(m);
    setCurrYear(y);
  };

  const changeYear = (delta) => setCurrYear((y) => y + delta);
  const goView = (nextView) => setViewMode(nextView);

  const handleMonthSelect = (m) => {
    setCurrMonth(m);
    goView("days");
  };
  const handleYearSelect = (y) => {
    setCurrYear(y);
    goView("months");
  };

  const selectDate = (date) => {
    if (!date) return;
    const clamped = clampDate(date, minDate, maxDate);
    const formatted = formatDateStr(clamped, format);

    if (!isControlled) setSelectedStr(formatted);
    onChange?.(formatted, clamped);
    closeCalendar();
  };

  const resetDate = () => {
    if (!isControlled) setSelectedStr("");
    onChange?.("", null);
    closeCalendar();
  };

  const selectToday = () => {
    const t = clampDate(today, minDate, maxDate);
    setCurrYear(t.getFullYear());
    setCurrMonth(t.getMonth());
    selectDate(t);
  };

  const onKeyDown = (e) => {
    if (!open) {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        openCalendar();
      }
      return;
    }

    if (e.key === "Escape") {
      e.preventDefault();
      closeCalendar();
      return;
    }

    const base = focusDate || selectedDate || today;
    let next = new Date(base.getFullYear(), base.getMonth(), base.getDate());

    if (e.key === "ArrowLeft") {
      e.preventDefault();
      next.setDate(next.getDate() - 1);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      next.setDate(next.getDate() + 1);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      next.setDate(next.getDate() - 7);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      next.setDate(next.getDate() + 7);
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (isInRange(base)) selectDate(base);
      return;
    } else return;

    setFocusDate(next);
    setCurrYear(next.getFullYear());
    setCurrMonth(next.getMonth());
  };

  const selectedStringCompare = useMemo(() => {
    return selectedDate ? formatDateStr(selectedDate, "dd-mm-yyyy") : "";
  }, [selectedDate]);

  const weekHeader = useMemo(() => {
    return daysOfWeek
      .slice(weekStartsOn)
      .concat(daysOfWeek.slice(0, weekStartsOn));
  }, [daysOfWeek, weekStartsOn]);

  const panelVariants = {
    hidden: () => ({ opacity: 0, scale: 1 }),
    show: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.16, ease: "easeOut" },
    },
    exit: () => ({
      opacity: 0,
      scale: 1,
      transition: { duration: 0.12, ease: "easeIn" },
    }),
  };

  const fadeVariants = {
    enter: () => ({ opacity: 0 }),
    center: { opacity: 1, transition: { duration: 0.14, ease: "easeOut" } },
    exit: () => ({
      opacity: 0,
      transition: { duration: 0.12, ease: "easeIn" },
    }),
  };

  const GRID_H_DAYS = 6 * 36 + 16;
  const GRID_H_MONTHS = 4 * 44;
  const GRID_H_YEARS = 4 * 44;

  const panelNode =
    open && portalRoot ? (
      <AnimatePresence>
        <motion.div
          key="panel"
          ref={panelRef}
          role="dialog"
          aria-label="Date picker"
          className="fixed bg-white border border-gray-200 rounded-lg shadow-lg z-[9999] will-change-transform"
          style={{
            top: panelPos.top,
            left: panelPos.left,
            width: panelPos.width,
          }}
          initial="hidden"
          animate="show"
          exit="exit"
          variants={panelVariants}
        >
          {/* DAYS VIEW */}
          {viewMode === "days" && (
            <>
              <div className="flex justify-between items-center px-2 py-2 bg-blue-500 rounded-t-lg text-white">
                <button
                  onClick={() => changeMonth(-1)}
                  className="px-2 py-1 hover:bg-blue-400 rounded-lg active:scale-95 transition"
                  aria-label="Prev month"
                  type="button"
                >
                  <img
                    src="/icons/right-arrow.svg"
                    alt="Prev"
                    className="shrink-0 rotate-180"
                  />
                </button>

                <button
                  className="font-semibold cursor-pointer hover:opacity-90 transition"
                  onClick={() => goView("months")}
                  aria-label="Switch to months"
                  type="button"
                >
                  {months[currMonth]} {currYear}
                </button>

                <button
                  onClick={() => changeMonth(1)}
                  className="px-2 py-1 hover:bg-blue-400 rounded-lg active:scale-95 transition"
                  aria-label="Next month"
                  type="button"
                >
                  <img
                    src="/icons/right-arrow.svg"
                    alt="Next"
                    className="shrink-0"
                  />
                </button>
              </div>

              <div className="grid grid-cols-7 text-center font-semibold text-gray-700 px-2 pt-2">
                {weekHeader.map((d) => (
                  <div key={d}>{d}</div>
                ))}
              </div>

              <div
                className="relative overflow-hidden"
                style={{ height: GRID_H_DAYS }}
              >
                <AnimatePresence mode="wait">
                  <motion.div
                    key={`${currYear}-${currMonth}`}
                    variants={fadeVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    className="absolute inset-0 grid grid-cols-7 text-center text-gray-700 p-2 gap-y-1"
                  >
                    {daysGrid.map((date, idx) => {
                      if (!date)
                        return <div key={`empty-${idx}`} className="p-2" />;

                      const isSelected =
                        selectedStringCompare &&
                        selectedStringCompare ===
                          formatDateStr(date, "dd-mm-yyyy");

                      const disabled = !isInRange(date);

                      const isFocus =
                        focusDate &&
                        formatDateStr(focusDate, "dd-mm-yyyy") ===
                          formatDateStr(date, "dd-mm-yyyy");

                      return (
                        <button
                          key={date.toISOString()}
                          type="button"
                          disabled={disabled}
                          onClick={() => !disabled && selectDate(date)}
                          className={[
                            "relative p-2 rounded-lg cursor-pointer outline-none",
                            "focus:ring-2 focus:ring-blue-300",
                            disabled
                              ? "text-gray-300 cursor-default"
                              : "hover:bg-blue-100",
                            isSelected
                              ? "bg-blue-500 text-white hover:bg-blue-600"
                              : "",
                            isFocus && !isSelected
                              ? "ring-2 ring-blue-300"
                              : "",
                            "transition-colors",
                          ].join(" ")}
                        >
                          {date.getDate()}
                          {isSelected && (
                            <span className="absolute inset-0 rounded-lg ring-2 ring-blue-300/70" />
                          )}
                        </button>
                      );
                    })}
                  </motion.div>
                </AnimatePresence>
              </div>
            </>
          )}

          {/* MONTHS VIEW */}
          {viewMode === "months" && (
            <>
              <div className="flex justify-between items-center px-2 py-2 bg-blue-500 rounded-t-lg text-white">
                <button
                  onClick={() => changeYear(-1)}
                  className="px-2 py-1 hover:bg-blue-400 rounded-lg active:scale-95 transition"
                  aria-label="Prev year"
                  type="button"
                >
                  <img
                    src="/icons/right-arrow.svg"
                    alt="Prev year"
                    className="rotate-180"
                  />
                </button>

                <button
                  className="font-semibold cursor-pointer hover:opacity-90 transition"
                  onClick={() => goView("years")}
                  aria-label="Switch to years"
                  type="button"
                >
                  {currYear}
                </button>

                <button
                  onClick={() => changeYear(1)}
                  className="px-2 py-1 hover:bg-blue-400 rounded-lg active:scale-95 transition"
                  aria-label="Next year"
                  type="button"
                >
                  <img src="/icons/right-arrow.svg" alt="Next year" />
                </button>
              </div>

              <div
                className="relative overflow-hidden"
                style={{ height: GRID_H_MONTHS }}
              >
                <AnimatePresence mode="wait">
                  <motion.div
                    key={`months-${currYear}`}
                    variants={fadeVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    className="absolute inset-0 grid grid-cols-3 p-3"
                  >
                    {months.map((m, i) => (
                      <motion.button
                        key={m}
                        type="button"
                        className="p-2 text-center text-gray-700 hover:bg-blue-100 rounded-lg active:scale-95 transition"
                        onClick={() => handleMonthSelect(i)}
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.96 }}
                      >
                        {m}
                      </motion.button>
                    ))}
                  </motion.div>
                </AnimatePresence>
              </div>
            </>
          )}

          {/* YEARS VIEW */}
          {viewMode === "years" && (
            <>
              <div className="flex justify-between items-center px-2 py-2 bg-blue-500 rounded-t-lg text-white">
                <button
                  onClick={() => changeYear(-10)}
                  className="px-2 py-1 hover:bg-blue-400 rounded-lg active:scale-95 transition"
                  aria-label="Prev decade"
                  type="button"
                >
                  <img
                    src="/icons/right-arrow.svg"
                    alt="Prev decade"
                    className="rotate-180"
                  />
                </button>

                <span className="font-semibold">
                  {decadeYears(currYear)[0]} - {decadeYears(currYear)[11]}
                </span>

                <button
                  onClick={() => changeYear(10)}
                  className="px-2 py-1 hover:bg-blue-400 rounded-lg active:scale-95 transition"
                  aria-label="Next decade"
                  type="button"
                >
                  <img src="/icons/right-arrow.svg" alt="Next decade" />
                </button>
              </div>

              <div
                className="relative overflow-hidden"
                style={{ height: GRID_H_YEARS }}
              >
                <AnimatePresence mode="wait">
                  <motion.div
                    key={`years-${Math.floor(currYear / 10)}`}
                    variants={fadeVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    className="absolute inset-0 grid grid-cols-3 p-3"
                  >
                    {decadeYears(currYear).map((y) => (
                      <motion.button
                        key={y}
                        type="button"
                        className="p-2 text-center text-gray-700 hover:bg-blue-100 rounded-lg active:scale-95 transition"
                        onClick={() => handleYearSelect(y)}
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.96 }}
                      >
                        {y}
                      </motion.button>
                    ))}
                  </motion.div>
                </AnimatePresence>
              </div>
            </>
          )}

          {/* Actions */}
          <div className="flex justify-between p-2 border-t">
            <button
              onClick={resetDate}
              className="px-3 py-1 rounded-lg text-red-500 hover:bg-red-100 active:scale-95 transition"
              type="button"
            >
              {clearLabel}
            </button>
            <button
              onClick={selectToday}
              className="px-3 py-1 rounded-lg bg-blue-500 text-white hover:bg-blue-600 active:scale-95 transition"
              type="button"
            >
              {todayLabel}
            </button>
          </div>
        </motion.div>
      </AnimatePresence>
    ) : null;

  return (
    <div
      className="relative inline-block min-w-full"
      ref={containerRef}
      onKeyDown={onKeyDown}
    >
      {/* Input */}
      <button
        ref={controlRef}
        type="button"
        className={`flex items-center justify-between border rounded-lg px-3 bg-white w-full text-left transition-[box-shadow,transform,border-color] duration-150 active:scale-[0.995] ${
          open
            ? "border-2 border-blue-500 shadow-md"
            : "border-gray-300 hover:shadow-sm"
        }`}
        style={{ width, height }}
        onClick={toggleCalendar}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <span className={`truncate ${!selectedStr ? "text-gray-400" : ""}`}>
          {selectedStr || placeholder}
        </span>

        {selectedStr ? (
          <img
            src="/icons/x-close.svg"
            alt="Clear"
            onClick={(e) => {
              e.stopPropagation();
              resetDate();
            }}
            className="w-5 h-5 opacity-80 hover:opacity-100 transition-opacity"
          />
        ) : (
          <img
            src="/icons/calendar.svg"
            alt="Calendar"
            className="w-5 h-5 opacity-80"
          />
        )}
      </button>

      {/* Portal Panel */}
      {portalRoot && panelNode ? createPortal(panelNode, portalRoot) : null}
    </div>
  );
};

export default DatePicker;
